/**
 * PhenologyDockPanel.ts
 *
 * Dedicated full-width bottom calendar dock for Seasonal & Phenology products:
 * - Fall Foliage & Phenology (foliage)
 * - Alpine Wildflowers & Phenology (alpine_wildflowers)
 * - Desert Superbloom & Annuals (desert_wildflowers)
 * - Wild Berry Ripening & Foraging (wild_berries)
 * - Wild Mushroom Foraging (mushrooms)
 *
 * When open, docks across full viewport width at the bottom, replacing the master
 * timeline and collapsing the trend chart for maximum legibility and comfort.
 */

import store, { AppState } from '../../state';
import { FoliageService } from '../../services/FoliageService';
import { WildflowerService } from '../../services/WildflowerService';
import { MushroomService, SPECIES_CATALOG } from '../../services/MushroomService';
import { getElevationAt } from '../../services/PoiDataService';

export class PhenologyDockPanel {
  private static instance: PhenologyDockPanel | null = null;
  private container: HTMLElement | null = null;
  private currentProduct: string = 'foliage';
  private isCollapsed: boolean = false;
  private unsubscribe: (() => void) | null = null;
  private lastPoiKey: string = '';
  private currentElevation: number = 1200;
  private sampleSessionId: number = 0;

  public static getInstance(): PhenologyDockPanel {
    if (!PhenologyDockPanel.instance) {
      PhenologyDockPanel.instance = new PhenologyDockPanel();
    }
    return PhenologyDockPanel.instance;
  }

  public open(productKey: string = 'foliage'): void {
    this.currentProduct = productKey;
    if (!this.container) {
      this.initDom();
    }
    if (this.container) {
      this.container.style.display = 'flex';
      this.container.style.flexDirection = 'column';
    }

    // Hide master timeline to avoid collision
    const timelineEl = document.getElementById('timeline-container');
    if (timelineEl) {
      timelineEl.style.display = 'none';
    }

    // Collapse docked trend chart to maximize calendar visibility
    store.update({ dockedTrendCollapsed: true });

    if (!this.unsubscribe) {
      this.unsubscribe = store.subscribe((state) => {
        if (this.container && this.container.style.display !== 'none') {
          this.render(state);
        }
      });
    }

    this.render(store.getState());
  }

  public close(): void {
    if (this.container) {
      this.container.style.display = 'none';
    }
    // Restore master timeline
    const timelineEl = document.getElementById('timeline-container');
    if (timelineEl) {
      timelineEl.style.display = '';
    }
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  public isOpen(): boolean {
    return !!this.container && this.container.style.display !== 'none';
  }

  private initDom(): void {
    this.container = document.createElement('div');
    this.container.id = 'phenology-bottom-dock';
    this.container.className = 'phenology-bottom-dock';
    this.container.style.display = 'none';
    document.body.appendChild(this.container);
  }

  public render(state: AppState): void {
    if (!this.container) return;
    const prod = this.currentProduct;
    const poi = state.selectedPoi;
    const lat = poi ? poi.lat : state.mapCenter[1];
    const lon = poi ? poi.lng : state.mapCenter[0];
    const elev = poi && poi.elev ? poi.elev : this.currentElevation;
    const elevMeters = elev / 3.28084;
    const year = 2026;

    const locKey = `${lat.toFixed(4)}_${lon.toFixed(4)}`;
    if (locKey !== this.lastPoiKey) {
      this.lastPoiKey = locKey;
      if (poi && poi.elev && poi.elev > 0) {
        this.currentElevation = poi.elev;
      } else {
        getElevationAt(lat, lon).then(prof => {
          if (prof && prof.elevation > 0 && prof.elevation !== this.currentElevation) {
            this.currentElevation = prof.elevation;
            if (poi) {
              store.update({ selectedPoi: { ...poi, elev: prof.elevation } });
            }
          }
        });
      }
    }

    const units = state.units || 'imperial';
    const elevFt = Math.round(elev);
    const elevDisplay = units === 'metric' ? `${Math.round(elevMeters)}m` : `${elevFt.toLocaleString()}ft`;

    // 1. Product Metadata & Progression
    let prodTitle = 'Seasonal & Phenology Calendar';
    let prodIcon = '🌿';
    let selectedDateStr = '';
    let monthTabLabels: string[] = [];
    let currentMonthIdx = 0;
    let calendarDays: any[] = [];
    let firstDayCol = 0;
    let monthTitle = '';
    let peakSummaryText = '';
    let availableSpecies: Array<{ id: string; name: string }> = [];
    let currentSpeciesId = 'composite';
    let legendItems: Array<{ label: string; color: string }> = [];

    if (prod === 'foliage') {
      prodTitle = 'Fall Foliage & Phenology Calendar';
      prodIcon = '🍁';
      const defaultInfo = FoliageService.getDefaultDateInfo();
      currentMonthIdx = state.foliageMonthIndex !== undefined ? state.foliageMonthIndex : defaultInfo.monthIndex;
      selectedDateStr = state.foliageSelectedDate || defaultInfo.dateStr;
      monthTabLabels = ['Sep', 'Oct', 'Nov'];
      const cal = FoliageService.getMonthCalendar(currentMonthIdx, lat, lon, elevMeters, year);
      calendarDays = cal.days;
      firstDayCol = cal.firstDayCol;
      monthTitle = cal.monthName;
      peakSummaryText = cal.profile.peakDateRangeLabel ? `Peak: ${cal.profile.peakDateRangeLabel.replace(/^Peak:\s*/i, '')}` : '';
      legendItems = [
        { label: 'Patchy', color: '#2e7d32' },
        { label: 'Moderate', color: '#fbc02d' },
        { label: 'Near Peak', color: '#f57c00' },
        { label: 'Peak Foliage', color: '#d32f2f' },
        { label: 'Past Peak', color: '#795548' },
        { label: 'Bare Canopy', color: '#78909c' }
      ];
    } else if (prod === 'alpine_wildflowers' || prod === 'desert_wildflowers' || prod === 'wild_berries') {
      const defaultInfo = WildflowerService.getDefaultDateInfo(prod);
      if (prod === 'desert_wildflowers') {
        prodTitle = 'Desert Superbloom & Annuals Calendar';
        prodIcon = '🏜️';
        currentMonthIdx = state.desertWildflowerMonthIndex !== undefined ? state.desertWildflowerMonthIndex : defaultInfo.monthIndex;
        selectedDateStr = state.desertWildflowerSelectedDate || defaultInfo.dateStr;
        currentSpeciesId = state.desertWildflowerSpecies || 'composite';
        monthTabLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May'];
        availableSpecies = [
          { id: 'composite', name: 'Desert Superbloom (All Species)' },
          { id: 'desert_gold', name: 'Desert Gold (Geraea canescens)' },
          { id: 'sand_verbena', name: 'Sand Verbena (Abronia villosa)' },
          { id: 'desert_dune_primrose', name: 'Dune Primrose (Oenothera deltoides)' },
          { id: 'chuparosa', name: 'Chuparosa (Justicia californica)' }
        ];
        legendItems = [
          { label: 'Dormant Seed', color: '#64748b' },
          { label: 'Germination', color: '#81c784' },
          { label: 'Early Blooms', color: '#cddc39' },
          { label: 'Peak Carpet', color: '#e91e63' },
          { label: 'Past Peak', color: '#ff9800' },
          { label: 'Quiescence', color: '#90a4ae' }
        ];
      } else if (prod === 'wild_berries') {
        prodTitle = 'Wild Berry Ripening & Foraging Calendar';
        prodIcon = '🫐';
        currentMonthIdx = state.wildBerryMonthIndex !== undefined ? state.wildBerryMonthIndex : defaultInfo.monthIndex;
        selectedDateStr = state.wildBerrySelectedDate || defaultInfo.dateStr;
        currentSpeciesId = state.wildBerrySpecies || 'composite';
        monthTabLabels = ['Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
        availableSpecies = [
          { id: 'composite', name: 'All Berry Species (Composite)' },
          { id: 'huckleberry', name: 'Mountain Huckleberry (Vaccinium membr.)' },
          { id: 'blackberry', name: 'Pacific Blackberry (Rubus ursinus)' },
          { id: 'salmonberry', name: 'Salmonberry (Rubus spectabilis)' },
          { id: 'thimbleberry', name: 'Thimbleberry (Rubus parviflorus)' }
        ];
        legendItems = [
          { label: 'Dormant', color: '#64748b' },
          { label: 'Flowering', color: '#cddc39' },
          { label: 'Green Fruit', color: '#81c784' },
          { label: 'Early Ripe', color: '#ba68c8' },
          { label: 'Peak Sugar', color: '#7b1fa2' },
          { label: 'Dropped', color: '#8d6e63' }
        ];
      } else {
        prodTitle = 'Alpine Wildflowers & Phenology Calendar';
        prodIcon = '🌸';
        currentMonthIdx = state.alpineWildflowerMonthIndex !== undefined ? state.alpineWildflowerMonthIndex : (state.wildflowerMonthIndex !== undefined ? state.wildflowerMonthIndex : defaultInfo.monthIndex);
        selectedDateStr = state.alpineWildflowerSelectedDate || state.wildflowerSelectedDate || defaultInfo.dateStr;
        currentSpeciesId = state.alpineWildflowerSpecies || state.wildflowerSpecies || 'composite';
        monthTabLabels = ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
        availableSpecies = [
          { id: 'composite', name: 'All Alpine Species (Peak Floral Carpet)' },
          { id: 'avens', name: 'Alpine Avens (Geum rossii)' },
          { id: 'paintbrush', name: 'Subalpine Paintbrush (Castilleja rhexifolia)' },
          { id: 'lupine', name: 'Silky Lupine (Lupinus sericeus)' },
          { id: 'gentian', name: 'Explorer\'s Gentian (Gentiana calycosa)' }
        ];
        legendItems = [
          { label: 'Snowbound', color: '#a0d2f5' },
          { label: 'Vegetative', color: '#81c784' },
          { label: 'Early Bloom', color: '#cddc39' },
          { label: 'Peak Display', color: '#e91e63' },
          { label: 'Past Peak', color: '#ff9800' },
          { label: 'Bloom Ended', color: '#64748b' }
        ];
      }

      const cal = WildflowerService.getMonthCalendar(currentMonthIdx, lat, lon, elevMeters, currentSpeciesId, year, prod);
      calendarDays = cal.days;
      firstDayCol = cal.firstDayCol;
      monthTitle = cal.monthName;
      const targetCurve = cal.profile.speciesCurves.find(c => c.speciesId === currentSpeciesId) || cal.profile.speciesCurves[0];
      peakSummaryText = targetCurve ? `Peak: ${targetCurve.peakDateStr} (${targetCurve.name})` : '';
    } else if (prod === 'mushrooms') {
      prodTitle = 'Wild Mushroom Foraging Outlook';
      prodIcon = '🍄';
      selectedDateStr = 'Active Season (Today)';
      legendItems = [
        { label: 'Quiescent', color: '#64748b' },
        { label: 'Mycelial Prime', color: '#38bdf8' },
        { label: 'Pinning', color: '#4ade80' },
        { label: 'Active Flush', color: '#f59e0b' },
        { label: 'Peak Harvest', color: '#ef4444' }
      ];
    }

    // Active Selected Day info for the sidebar summary card
    const activeDay = calendarDays.find(d => d.dateStr === selectedDateStr) || calendarDays[0];
    const activeScore = activeDay ? (activeDay.fpi !== undefined ? activeDay.fpi : (activeDay.concurrencyPct !== undefined ? activeDay.concurrencyPct : 0)) : 0;
    const activeStage = activeDay ? activeDay.stageName : 'Forecast Day';
    const activeColor = activeDay ? (activeDay.colorHex || '#64748b') : '#64748b';

    // Map Display Mode status (Peak Date for country vs Daily Color Status for selected date vs anomaly layers)
    let isPeakDate = false;
    let statusModeKey = 'forecast';
    let statusModeLabel = '🍁 Daily Color Status';
    const foliageMode = state.foliageViewMode || 'peak_date';

    if (prod === 'foliage') {
      isPeakDate = foliageMode === 'peak_date';
      statusModeKey = 'forecast';
      statusModeLabel = '🍁 Daily Color Status';
    } else if (prod === 'desert_wildflowers') {
      const viewMode = state.desertWildflowerViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'status';
      statusModeLabel = '🏜️ Daily Bloom Status';
    } else if (prod === 'wild_berries') {
      const viewMode = state.wildBerryViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'status';
      statusModeLabel = '🫐 Daily Ripening Status';
    } else if (prod === 'alpine_wildflowers') {
      const viewMode = state.alpineWildflowerViewMode || state.wildflowerViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'forecast';
      statusModeLabel = '🌸 Daily Bloom Status';
    }

    // Top Bar Mode Buttons
    let topModeButtonsHtml = '';
    if (prod === 'foliage') {
      topModeButtonsHtml = `
        <button type="button" class="phenology-top-mode-btn ${foliageMode === 'peak_date' ? 'active' : ''}" data-mode="peak_date">
          📅 Peak Date
        </button>
        <button type="button" class="phenology-top-mode-btn ${foliageMode === 'forecast' ? 'active' : ''}" data-mode="forecast">
          🍁 Daily Status
        </button>
        <button type="button" class="phenology-top-mode-btn ${foliageMode === 'soil_moisture' ? 'active' : ''}" data-mode="soil_moisture">
          💧 Soil Moisture
        </button>
        <button type="button" class="phenology-top-mode-btn ${foliageMode === 'heat_stress' ? 'active' : ''}" data-mode="heat_stress">
          ☀️ Temp Anomaly
        </button>
        <button type="button" class="phenology-top-mode-btn ${foliageMode === 'vibrancy' ? 'active' : ''}" data-mode="vibrancy">
          ✨ Quality vs Normal
        </button>
      `;
    } else {
      topModeButtonsHtml = `
        <button type="button" class="phenology-top-mode-btn ${isPeakDate ? 'active' : ''}" data-mode="peak_date">
          📅 Peak Date Map
        </button>
        <button type="button" class="phenology-top-mode-btn ${!isPeakDate ? 'active' : ''}" data-mode="${statusModeKey}">
          ${statusModeLabel}
        </button>
      `;
    }

    // 2. Build Top Bar HTML (with mode toggle and prominent collapse & close buttons)
    const topBarHtml = `
      <div class="phenology-dock-top-bar">
        <div class="phenology-top-bar-left">
          <span class="phenology-top-icon">${prodIcon}</span>
          <span class="phenology-top-title">${prodTitle}</span>
          <span class="phenology-top-poi-pill">📍 ${lat.toFixed(3)}°N, ${Math.abs(lon).toFixed(3)}°W (${elevDisplay})</span>
        </div>
        ${prod !== 'mushrooms' ? `
          <div class="phenology-top-mode-toggle" title="Toggle map display mode">
            ${topModeButtonsHtml}
          </div>
        ` : ''}
        <div class="phenology-top-bar-right">
          <button type="button" class="phenology-collapse-btn" title="${this.isCollapsed ? 'Expand Calendar' : 'Collapse Calendar'}">
            ${this.isCollapsed ? '▲ Expand' : '▼ Collapse'}
          </button>
          <button type="button" class="phenology-close-btn" title="Close Seasonal Calendar Dock">
            ✕ Close Dock
          </button>
        </div>
      </div>
    `;

    // 3. Build Vertically Stacked Sidebar Controls HTML
    let legendCardHtml = '';
    if (prod === 'foliage') {
      if (foliageMode === 'peak_date') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Peak Arrival Date</div>
            <div class="phenology-legend-desc">Calendar dates mapped to colors on map</div>
            <div class="phenology-date-gradient-bar"></div>
            <div class="phenology-date-ticks">
              <span style="color: #4caf50;">Sept 15</span>
              <span style="color: #8bc34a;">Sept 25</span>
              <span style="color: #fbc02d;">Oct 5</span>
              <span style="color: #f57c00;">Oct 15</span>
              <span style="color: #ef5350;">Oct 25</span>
              <span style="color: #8d6e63;">Nov 5</span>
              <span style="color: #90a4ae;">Nov 15</span>
            </div>
            <div class="phenology-legend-grid">
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #2e7d32;"></span><span class="phenology-legend-label">≤ Sept 15 (Early Peak)</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #8bc34a;"></span><span class="phenology-legend-label">Late September</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #fbc02d;"></span><span class="phenology-legend-label">Early October</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #f57c00;"></span><span class="phenology-legend-label">Mid October Peak</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #ef5350;"></span><span class="phenology-legend-label">Late October Peak</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #795548;"></span><span class="phenology-legend-label">Early November</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #78909c;"></span><span class="phenology-legend-label">≥ Nov 15 (Late Peak)</span></div>
            </div>
          </div>
        `;
      } else if (foliageMode === 'soil_moisture') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Soil Moisture Anomaly</div>
            <div class="phenology-legend-desc">Standardized Soil Moisture Anomaly (SMA) measuring root-zone drought</div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #7f1d1d 0%, #d97706 25%, #eab308 42%, #cbd5e1 52%, #22c55e 75%, #2563eb 100%); border: 1px solid rgba(255,255,255,0.18); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: #94a3b8; font-weight: 700; margin-bottom: 6px;">
              <span style="color: #f87171;">D4 Exceptional</span>
              <span style="color: #fbbf24;">D2 Severe</span>
              <span style="color: #cbd5e1;">Normal</span>
              <span style="color: #4ade80;">Moist</span>
              <span style="color: #60a5fa;">Surplus</span>
            </div>
            <div class="phenology-legend-grid">
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #7f1d1d;"></span><span class="phenology-legend-label">D4 Exceptional Drought</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #d97706;"></span><span class="phenology-legend-label">D2 Severe Drought</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #cbd5e1;"></span><span class="phenology-legend-label">Near Normal Baseline</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #22c55e;"></span><span class="phenology-legend-label">Moist / Favorable</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #2563eb;"></span><span class="phenology-legend-label">Saturated Surplus</span></div>
            </div>
          </div>
        `;
      } else if (foliageMode === 'heat_stress') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Temperature Anomaly</div>
            <div class="phenology-legend-desc">Summer mean temperature departure (°C) vs 30-year normals</div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #1e40af 0%, #38bdf8 20%, #e2e8f0 38%, #fbbf24 55%, #f97316 70%, #dc2626 85%, #881337 100%); border: 1px solid rgba(255,255,255,0.18); margin-bottom: 4px;"></div>
            <div style="display: flex; justify-content: space-between; font-size: 0.62rem; color: #94a3b8; font-weight: 700; margin-bottom: 6px;">
              <span style="color: #93c5fd;">-4°C</span>
              <span style="color: #e2e8f0;">0°C (Norm)</span>
              <span style="color: #fde047;">+1.5°C</span>
              <span style="color: #fb923c;">+3.0°C</span>
              <span style="color: #f87171;">+4.5°C</span>
              <span style="color: #f43f5e;">+6.0°C</span>
            </div>
            <div class="phenology-legend-grid">
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #1e40af;"></span><span class="phenology-legend-label">Cooler Than Normal</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #e2e8f0;"></span><span class="phenology-legend-label">Near Normal Baseline</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #fbbf24;"></span><span class="phenology-legend-label">Moderate Heat (+1.5°C)</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #f97316;"></span><span class="phenology-legend-label">Elevated Heat (+3.0°C)</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #dc2626;"></span><span class="phenology-legend-label">Severe Heat (+4.5°C)</span></div>
              <div class="phenology-legend-item"><span class="phenology-legend-dot" style="background: #881337;"></span><span class="phenology-legend-label">Extreme Heat (>+4.5°C)</span></div>
            </div>
          </div>
        `;
      } else if (foliageMode === 'vibrancy') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Projected Foliage Quality</div>
            <div class="phenology-legend-desc">Anticipated peak brilliance and saturation departure vs typical autumn</div>
            <div style="display: flex; flex-direction: column; gap: 6px; margin-top: 4px;">
              <div class="phenology-legend-item" style="align-items: flex-start;">
                <span class="phenology-legend-dot" style="background: #e53935; margin-top: 3px;"></span>
                <div>
                  <span class="phenology-legend-label" style="color: #ff8a80; font-weight: 700;">Above Normal / Enhanced Vibrancy</span>
                  <div style="font-size: 0.62rem; color: #94a3b8; line-height: 1.25;">Crisp 34-45°F nights & sun promote intense red anthocyanin.</div>
                </div>
              </div>
              <div class="phenology-legend-item" style="align-items: flex-start;">
                <span class="phenology-legend-dot" style="background: #ffb300; margin-top: 3px;"></span>
                <div>
                  <span class="phenology-legend-label" style="color: #ffd54f; font-weight: 700;">Average / Typical Vibrancy</span>
                  <div style="font-size: 0.62rem; color: #94a3b8; line-height: 1.25;">Expected baseline autumn coloration matching 30-year climate normals.</div>
                </div>
              </div>
              <div class="phenology-legend-item" style="align-items: flex-start;">
                <span class="phenology-legend-dot" style="background: #8d6e63; margin-top: 3px;"></span>
                <div>
                  <span class="phenology-legend-label" style="color: #bcaaa4; font-weight: 700;">Below Normal / Reduced Vibrancy</span>
                  <div style="font-size: 0.62rem; color: #94a3b8; line-height: 1.25;">Warm nights, drought scorch, or rain mute brilliance.</div>
                </div>
              </div>
            </div>
          </div>
        `;
      } else {
        // Daily status mode ('forecast')
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Daily Status (${selectedDateStr})</div>
            <div class="phenology-legend-desc">Colors mapped to stage of phenology for selected date</div>
            <div class="phenology-stage-gradient-bar"></div>
            <div class="phenology-legend-grid">
              ${legendItems.map(item => `
                <div class="phenology-legend-item">
                  <span class="phenology-legend-dot" style="background: ${item.color};"></span>
                  <span class="phenology-legend-label">${item.label}</span>
                </div>
              `).join('')}
            </div>
          </div>
        `;
      }
    } else if (isPeakDate) {
      if (prod === 'desert_wildflowers') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Peak Bloom Date</div>
            <div class="phenology-legend-desc">Calendar dates mapped to bloom arrival on map</div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.18); margin-bottom: 4px;"></div>
            <div class="phenology-date-ticks">
              <span style="color: #cddc39;">Feb 15</span>
              <span style="color: #ffd54f;">Mar 1</span>
              <span style="color: #ff9800;">Mar 20</span>
              <span style="color: #ff4081;">Apr 10</span>
              <span style="color: #b388ff;">Apr 30</span>
            </div>
          </div>
        `;
      } else if (prod === 'wild_berries') {
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Peak Ripening Date</div>
            <div class="phenology-legend-desc">Calendar dates mapped to ripening on map</div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #ba68c8 75%, #7b1fa2 100%); border: 1px solid rgba(255,255,255,0.18); margin-bottom: 4px;"></div>
            <div class="phenology-date-ticks">
              <span style="color: #cddc39;">Jun 20</span>
              <span style="color: #ffd54f;">Jul 10</span>
              <span style="color: #ff9800;">Aug 1</span>
              <span style="color: #ba68c8;">Aug 20</span>
              <span style="color: #b388ff;">Sep 15</span>
            </div>
          </div>
        `;
      } else {
        // alpine_wildflowers
        legendCardHtml = `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Color Legend: Peak Bloom Date</div>
            <div class="phenology-legend-desc">Calendar dates mapped to bloom carpet on map</div>
            <div style="height: 10px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.18); margin-bottom: 4px;"></div>
            <div class="phenology-date-ticks">
              <span style="color: #cddc39;">May 15</span>
              <span style="color: #ffd54f;">Jun 1</span>
              <span style="color: #ff9800;">Jun 20</span>
              <span style="color: #ff4081;">Jul 10</span>
              <span style="color: #b388ff;">Aug 1</span>
            </div>
          </div>
        `;
      }
    } else {
      legendCardHtml = `
        <div class="phenology-card">
          <div class="phenology-card-title">Map Color Legend: Daily Status (${selectedDateStr})</div>
          <div class="phenology-legend-desc">Colors mapped to stage of phenology for selected date</div>
          <div class="phenology-stage-gradient-bar"></div>
          <div class="phenology-legend-grid">
            ${legendItems.map(item => `
              <div class="phenology-legend-item">
                <span class="phenology-legend-dot" style="background: ${item.color};"></span>
                <span class="phenology-legend-label">${item.label}</span>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Sidebar Mode Buttons and Hint text
    let sidebarModeButtonsHtml = '';
    let modeHintText = '';
    if (prod === 'foliage') {
      sidebarModeButtonsHtml = `
        <button type="button" class="phenology-mode-btn ${foliageMode === 'peak_date' ? 'active' : ''}" data-mode="peak_date">
          📅 Peak Date Map
        </button>
        <button type="button" class="phenology-mode-btn ${foliageMode === 'forecast' ? 'active' : ''}" data-mode="forecast">
          🍁 Daily Color Status
        </button>
        <button type="button" class="phenology-mode-btn ${foliageMode === 'soil_moisture' ? 'active' : ''}" data-mode="soil_moisture">
          💧 Soil Moisture Anomaly
        </button>
        <button type="button" class="phenology-mode-btn ${foliageMode === 'heat_stress' ? 'active' : ''}" data-mode="heat_stress">
          ☀️ Temperature Anomaly
        </button>
        <button type="button" class="phenology-mode-btn ${foliageMode === 'vibrancy' ? 'active' : ''}" data-mode="vibrancy" style="grid-column: span 2;">
          ✨ Projected Quality vs Normal
        </button>
      `;
      if (foliageMode === 'peak_date') {
        modeHintText = 'Showing peak date for entire country (calendar dates mapped to map colors).';
      } else if (foliageMode === 'forecast') {
        modeHintText = `Showing color status on map for ${selectedDateStr}.`;
      } else if (foliageMode === 'soil_moisture') {
        modeHintText = 'Showing summer root-zone soil moisture anomaly (drought vs favorable moisture).';
      } else if (foliageMode === 'heat_stress') {
        modeHintText = 'Showing summer temperature departures (°C) against 30-year climate normals.';
      } else if (foliageMode === 'vibrancy') {
        modeHintText = 'Showing projected autumn color vibrancy and brilliance compared to normal.';
      }
    } else {
      sidebarModeButtonsHtml = `
        <button type="button" class="phenology-mode-btn ${isPeakDate ? 'active' : ''}" data-mode="peak_date">
          📅 Peak Date Map
        </button>
        <button type="button" class="phenology-mode-btn ${!isPeakDate ? 'active' : ''}" data-mode="${statusModeKey}">
          ${statusModeLabel}
        </button>
      `;
      modeHintText = isPeakDate 
        ? 'Showing peak date for entire country (calendar dates mapped to map colors).' 
        : `Showing color status on map for ${selectedDateStr}.`;
    }

    const sidebarHtml = `
      <aside class="phenology-sidebar-panel">
        <!-- POI Details Card -->
        <div class="phenology-card">
          <div class="phenology-card-title">📍 Location & Elevation</div>
          <div class="phenology-poi-coords">${lat.toFixed(3)}°N, ${Math.abs(lon).toFixed(3)}°W • ${elevDisplay}</div>
          ${peakSummaryText ? `<div class="phenology-peak-badge">${peakSummaryText}</div>` : ''}
        </div>

        <!-- Map Display Mode Card -->
        ${prod !== 'mushrooms' ? `
          <div class="phenology-card">
            <div class="phenology-card-title">Map Display Mode</div>
            <div class="phenology-mode-toggle-group">
              ${sidebarModeButtonsHtml}
            </div>
            <div class="phenology-mode-hint">
              ${modeHintText}
            </div>
          </div>
        ` : ''}

        <!-- Month Selector Card -->
        ${monthTabLabels.length > 0 ? `
          <div class="phenology-card">
            <div class="phenology-card-title">Calendar Month</div>
            <div class="phenology-month-tabs">
              ${monthTabLabels.map((label, idx) => `
                <button type="button" class="phenology-month-tab ${idx === currentMonthIdx ? 'active' : ''}" data-month="${idx}">
                  ${label}
                </button>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Species Selector Card -->
        ${availableSpecies.length > 0 ? `
          <div class="phenology-card">
            <div class="phenology-card-title">Species / Target</div>
            <select class="phenology-species-select" data-prod="${prod}">
              ${availableSpecies.map(sp => `
                <option value="${sp.id}" ${sp.id === currentSpeciesId ? 'selected' : ''}>${sp.name}</option>
              `).join('')}
            </select>
          </div>
        ` : ''}

        <!-- Selected Date Card -->
        ${prod !== 'mushrooms' ? `
          <div class="phenology-card">
            <div class="phenology-card-title">Selected Forecast Date</div>
            <div class="phenology-selected-date-row">
              <span class="phenology-selected-date-val">📅 ${selectedDateStr}</span>
            </div>
            <div class="phenology-selected-stage-row">
              <span id="phenology-sidebar-status-swatch" class="phenology-status-swatch" style="background: ${activeColor};"></span>
              <span id="phenology-sidebar-status-val" class="phenology-status-text">${activeStage} (${activeScore}%)</span>
            </div>
          </div>
        ` : ''}

        <!-- Stage Guide / Map Color Legend Card -->
        ${legendCardHtml}
      </aside>
    `;

    // 4. Build Main Panel HTML (Calendar Grid or Mushroom Outlook)
    let bodyHtml = '';
    if (!this.isCollapsed) {
      if (prod === 'mushrooms') {
        const currentSpecies = state.mushroomSpecies || 'general_flush';
        const speciesInfo = SPECIES_CATALOG[currentSpecies] || SPECIES_CATALOG['general_flush'];
        const localScore = Math.min(100, Math.max(10, Math.round(55 + 30 * Math.sin(lat * 0.1) * Math.cos(lon * 0.1))));
        const stage = MushroomService.getStageInfo(localScore);

        bodyHtml = `
          <div class="phenology-mushroom-view">
            <div class="phenology-mushroom-card">
              <span class="phenology-mush-icon">🍄</span>
              <div class="phenology-mush-info">
                <h4>${speciesInfo.name} <em>(${speciesInfo.scientificName})</em></h4>
                <div class="phenology-mush-score-bar">
                  <div class="phenology-mush-score-fill" style="width: ${localScore}%; background: ${stage.color};"></div>
                </div>
                <div class="phenology-mush-readout">
                  <strong>Flush Score: ${localScore}/100</strong> • <span>${stage.stage}</span>
                </div>
                <p style="font-size: 0.76rem; color: #cbd5e1; margin-top: 4px;">${stage.advice}</p>
              </div>
            </div>

            <div class="phenology-mushroom-outlook-grid">
              ${[0, 1, 2, 3, 4, 5, 6].map(offset => {
                const d = new Date();
                d.setDate(d.getDate() + offset);
                const dayName = offset === 0 ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' });
                const dateNum = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
                const dayScore = Math.min(100, Math.max(10, Math.round(localScore + offset * 3 - Math.sin(offset) * 8)));
                const dayStage = MushroomService.getStageInfo(dayScore);
                return `
                  <div class="phenology-outlook-col ${offset === 0 ? 'today' : ''}">
                    <span class="phenology-outlook-day">${dayName}</span>
                    <span class="phenology-outlook-date">${dateNum}</span>
                    <span class="phenology-outlook-badge" style="background: ${dayStage.color}; color: #fff;">${dayScore}%</span>
                    <span class="phenology-outlook-label">${dayStage.stage}</span>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      } else {
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const totalEmptyCols = firstDayCol;
        const totalCells = totalEmptyCols + calendarDays.length;
        const totalRows = Math.ceil(totalCells / 7);

        let cellsHtml = '';
        for (let i = 0; i < totalEmptyCols; i++) {
          cellsHtml += `<div class="phenology-cal-cell empty"></div>`;
        }

        calendarDays.forEach((day) => {
          const isSelected = day.dateStr === selectedDateStr;
          const score = day.fpi !== undefined ? day.fpi : (day.concurrencyPct !== undefined ? day.concurrencyPct : (day.pct || day.score || 0));
          const stageName = day.stageName || (score >= 80 ? 'Peak' : (score >= 50 ? 'Near Peak' : (score >= 20 ? 'Early' : 'Dormant')));
          const badgeText = day.badgeLabel ? `${day.badgeLabel} (${score}%)` : `${stageName} (${score}%)`;
          const cellColor = day.colorHex || '#64748b';

          cellsHtml += `
            <div class="phenology-cal-cell ${isSelected ? 'selected' : ''}" data-date="${day.dateStr}" style="background: ${cellColor};" title="${day.dateStr}: ${stageName} (${score}%)">
              <div class="phenology-cell-top">
                <span class="phenology-day-num">${day.dayNum}</span>
                ${day.isToday ? '<span class="phenology-today-dot" title="Today"></span>' : ''}
              </div>
              <div class="phenology-cell-badge">
                ${badgeText}
              </div>
            </div>
          `;
        });

        const remainingCells = totalRows * 7 - totalCells;
        for (let i = 0; i < remainingCells; i++) {
          cellsHtml += `<div class="phenology-cal-cell empty"></div>`;
        }

        bodyHtml = `
          <div class="phenology-calendar-wrap">
            <div class="phenology-month-headline">${monthTitle}</div>
            <div class="phenology-weekday-header">
              ${weekDays.map(wd => `<div class="phenology-weekday-cell">${wd}</div>`).join('')}
            </div>
            <div class="phenology-grid">
              ${cellsHtml}
            </div>
          </div>
        `;
      }
    }

    this.container.innerHTML = `
      ${topBarHtml}
      <div class="phenology-dock-body" style="display: ${this.isCollapsed ? 'none' : 'block'};">
        <div class="phenology-dock-content">
          ${sidebarHtml}
          <main class="phenology-main-panel">
            ${bodyHtml}
          </main>
        </div>
      </div>
    `;

    this.bindEvents(prod, calendarDays);

    // Asynchronously sample exact map tile pixels at selected POI for Fall Foliage
    if (prod === 'foliage' && state.manifest && !this.isCollapsed) {
      const sessionId = ++this.sampleSessionId;
      const targetProduct = state.foliageTreedOnly ? 'foliage_treed' : 'foliage';
      FoliageService.sampleMonthTilePixels(
        state.manifest,
        currentMonthIdx,
        lat,
        lon,
        year,
        (dateStr, sampled) => {
          if (sessionId !== this.sampleSessionId || !this.container) return;
          const cell = this.container.querySelector(`.phenology-cal-cell[data-date="${dateStr}"]`) as HTMLElement | null;
          if (cell) {
            cell.style.background = sampled.colorHex;
            const badgeEl = cell.querySelector('.phenology-cell-badge');
            if (badgeEl) {
              const currentText = badgeEl.textContent || '';
              const pctMatch = currentText.match(/\((\d+%?)\)/);
              const scoreSuffix = pctMatch ? ` (${pctMatch[1]})` : '';
              badgeEl.textContent = `${sampled.badgeLabel || sampled.stageName}${scoreSuffix}`;
            }
          }
          if (dateStr === selectedDateStr) {
            const statusValEl = this.container.querySelector('#phenology-sidebar-status-val');
            if (statusValEl) {
              const currentVal = statusValEl.textContent || '';
              const pctMatch = currentVal.match(/\((\d+%?)\)/);
              const scoreSuffix = pctMatch ? ` (${pctMatch[1]})` : '';
              statusValEl.textContent = `${sampled.stageName}${scoreSuffix}`;
            }
            const statusSwatchEl = this.container.querySelector('#phenology-sidebar-status-swatch') as HTMLElement | null;
            if (statusSwatchEl) {
              statusSwatchEl.style.background = sampled.colorHex;
            }
          }
        },
        targetProduct
      );

      // Asynchronously sample exact peak arrival date from foliage_peak_date.pmtiles
      FoliageService.samplePeakDatePixel(state.manifest, lat, lon, !!state.foliageTreedOnly).then(sampled => {
        if (sampled && this.container) {
          const badgeEl = this.container.querySelector('.phenology-peak-badge');
          if (badgeEl) {
            badgeEl.textContent = `Peak: ${sampled.dateLabel}`;
          }
        }
      });
    }
  }

  private bindEvents(prod: string, calendarDays: any[] = []): void {
    if (!this.container) return;

    // Map Display Mode Toggle (Peak Date Map vs Daily Status Map)
    this.container.querySelectorAll('.phenology-mode-btn, .phenology-top-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (btn as HTMLElement).dataset.mode;
        if (!mode) return;
        const updates: any = {};
        if (prod === 'foliage') {
          updates.foliageViewMode = mode;
        } else if (prod === 'desert_wildflowers') {
          updates.desertWildflowerViewMode = mode;
        } else if (prod === 'wild_berries') {
          updates.wildBerryViewMode = mode;
        } else {
          updates.alpineWildflowerViewMode = mode;
          updates.wildflowerViewMode = mode;
        }
        store.update(updates);
      });
    });

    // Close button
    const closeBtn = this.container.querySelector('.phenology-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.close();
      });
    }

    // Collapse toggle button
    const collapseBtn = this.container.querySelector('.phenology-collapse-btn');
    if (collapseBtn) {
      collapseBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.isCollapsed = !this.isCollapsed;
        this.render(store.getState());
      });
    }

    // Month Tabs
    this.container.querySelectorAll('.phenology-month-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        const mIdx = Number((tab as HTMLElement).dataset.month);
        if (prod === 'foliage') {
          store.update({ foliageMonthIndex: mIdx });
        } else if (prod === 'desert_wildflowers') {
          store.update({ desertWildflowerMonthIndex: mIdx });
        } else if (prod === 'wild_berries') {
          store.update({ wildBerryMonthIndex: mIdx });
        } else {
          store.update({ alpineWildflowerMonthIndex: mIdx, wildflowerMonthIndex: mIdx });
        }
      });
    });

    // Species Selector
    const speciesSel = this.container.querySelector('.phenology-species-select') as HTMLSelectElement | null;
    if (speciesSel) {
      speciesSel.addEventListener('change', (e) => {
        e.stopPropagation();
        const spVal = speciesSel.value;
        if (prod === 'desert_wildflowers') {
          store.update({ desertWildflowerSpecies: spVal });
        } else if (prod === 'wild_berries') {
          store.update({ wildBerrySpecies: spVal });
        } else if (prod === 'mushrooms') {
          store.update({ mushroomSpecies: spVal });
        } else {
          store.update({ alpineWildflowerSpecies: spVal, wildflowerSpecies: spVal });
        }
      });
    }

    // Day Cells Click Selection
    this.container.querySelectorAll('.phenology-cal-cell:not(.empty)').forEach(cell => {
      cell.addEventListener('click', (e) => {
        e.stopPropagation();
        const dateStr = (cell as HTMLElement).dataset.date;
        if (!dateStr) return;

        // Visual active feedback immediately
        this.container?.querySelectorAll('.phenology-cal-cell').forEach(c => c.classList.remove('selected'));
        cell.classList.add('selected');

        // Immediate sidebar feedback
        const dayObj = calendarDays.find(d => d.dateStr === dateStr);
        if (dayObj) {
          const score = dayObj.fpi !== undefined ? dayObj.fpi : (dayObj.concurrencyPct !== undefined ? dayObj.concurrencyPct : 0);
          const statusValEl = this.container?.querySelector('#phenology-sidebar-status-val');
          if (statusValEl) {
            statusValEl.textContent = `${dayObj.stageName} (${score}%)`;
          }
          const statusSwatchEl = this.container?.querySelector('#phenology-sidebar-status-swatch') as HTMLElement | null;
          if (statusSwatchEl) {
            statusSwatchEl.style.background = (cell as HTMLElement).style.background || dayObj.colorHex;
          }
          const dateValEl = this.container?.querySelector('.phenology-selected-date-val');
          if (dateValEl) {
            dateValEl.textContent = `📅 ${dateStr}`;
          }
        }

        const updates: any = {};
        if (prod === 'foliage') {
          updates.foliageSelectedDate = dateStr;
          updates.foliageViewMode = 'forecast';
        } else if (prod === 'desert_wildflowers') {
          updates.desertWildflowerSelectedDate = dateStr;
          updates.desertWildflowerViewMode = 'status';
        } else if (prod === 'wild_berries') {
          updates.wildBerrySelectedDate = dateStr;
          updates.wildBerryViewMode = 'status';
        } else {
          updates.alpineWildflowerSelectedDate = dateStr;
          updates.wildflowerSelectedDate = dateStr;
          updates.alpineWildflowerViewMode = 'forecast';
          updates.wildflowerViewMode = 'forecast';
        }
        updates.currentTimeStep = new Date(`${dateStr}T12:00:00Z`);
        store.update(updates);
      });
    });
  }
}

export default PhenologyDockPanel;
